import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Divider,
  TableContainer,
  Button,
  Alert,
  Tooltip
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import TvIcon from '@mui/icons-material/Tv';
import TimerIcon from '@mui/icons-material/Timer';
import { useLanguage } from '../contexts/LanguageContext';
import { decodeState } from '../utils/stateManager';
import { FormatIcon } from './FormatIcons';
import { getStorageKey, TV_CHANNEL_NAME } from '../utils/tvSync';
import { subscribeTournamentUpdates } from '../utils/firebaseSync';
import ScoreTable from './ScoreTable';

function TvMode() {
  const { translate } = useLanguage();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { name, firebaseId } = useParams();
  const [liveState, setLiveState] = useState(null);
  const [localTimeLeft, setLocalTimeLeft] = useState(null);
  const channelRef = useRef(null);
  const firebaseUnsubscribeRef = useRef(null);
  const timerIntervalRef = useRef(null);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const encodedState = searchParams.get('state');

  const decodedState = useMemo(() => {
    if (!encodedState) return null;
    return decodeState(encodedState);
  }, [encodedState]);

  // Hydrate from URL first, then keep live updates in state
  useEffect(() => {
    if (decodedState) {
      setLiveState(decodedState);
    }
  }, [decodedState]);

  // Firebase real-time subscription
  useEffect(() => {
    if (!firebaseId) return;

    console.log('TV Mode subscribing to Firebase ID:', firebaseId);
    
    const unsubscribe = subscribeTournamentUpdates(firebaseId, (updatedState) => {
      if (updatedState) {
        setLiveState(updatedState);
      }
    });

    firebaseUnsubscribeRef.current = unsubscribe;

    return () => {
      if (firebaseUnsubscribeRef.current) {
        firebaseUnsubscribeRef.current();
      }
    };
  }, [firebaseId]);

  const {
    players = [],
    games = [],
    scores = {},
    playCount = {},
    wins = {},
    losses = {},
    draws = {},
    round = 1,
    format = 'americano',
    isComplete = false,
    roundType = 'points',
    timeLeft = null,
    roundEnded = false,
    lastGames = [],
    lastBenchPlayers = null,
    lastGameScores = {},
    name: tournamentName = '',
    playerStatus = {}
  } = liveState || {};

  const displayedGames = useMemo(
    () => (isComplete ? (lastGames || []) : games),
    [games, isComplete, lastGames]
  );

  const displayedBenchPlayersFromState = useMemo(
    () => (isComplete ? (lastBenchPlayers || null) : null),
    [isComplete, lastBenchPlayers]
  );

  // Document title effect - must come after tournamentName is defined
  useEffect(() => {
    const displayName = tournamentName || name;
    if (displayName) {
      document.title = `${displayName} • TV mode`;
    }
  }, [tournamentName, name]);

  // Local timer countdown - starts from Firebase timeLeft and counts down locally
  useEffect(() => {
    // Clear any existing timer
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    // Sync localTimeLeft with Firebase timeLeft when it changes
    if (timeLeft !== null && !roundEnded && !isComplete) {
      setLocalTimeLeft(timeLeft);
      
      // Start local countdown
      timerIntervalRef.current = setInterval(() => {
        setLocalTimeLeft(prev => {
          if (prev === null || prev <= 0) {
            clearInterval(timerIntervalRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else if (roundEnded || isComplete) {
      setLocalTimeLeft(timeLeft);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [timeLeft, roundEnded, isComplete, round]); // Re-sync when round changes too

  // Format time for display
  const formatTime = (seconds) => {
    if (seconds === null || seconds === undefined) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const leaderboard = useMemo(
    () => {
      // Handle both string player names and object format {i, n}
      const playerNames = players.map(p => typeof p === 'string' ? p : p?.n || '');
      
      return playerNames
        .filter(name => name) // Filter out empty names
        .map((playerName) => {
          // Get score - scores object has player names as keys
          const playerScore = scores && typeof scores === 'object' ? (scores[playerName] || 0) : 0;
          const playerRounds = playCount && typeof playCount === 'object' ? playCount[playerName] : null;
          const playerWins = wins && typeof wins === 'object' ? wins[playerName] : null;
          const playerLosses = losses && typeof losses === 'object' ? losses[playerName] : null;
          const playerDraws = draws && typeof draws === 'object' ? draws[playerName] : null;
          const playerLastGame = lastGameScores && typeof lastGameScores === 'object' ? lastGameScores[playerName] : null;
          
          return {
            name: playerName,
            score: typeof playerScore === 'number' ? playerScore : 0,
            rounds: typeof playerRounds === 'number' ? playerRounds : null,
            wins: typeof playerWins === 'number' ? playerWins : 0,
            losses: typeof playerLosses === 'number' ? playerLosses : 0,
            draws: typeof playerDraws === 'number' ? playerDraws : 0,
            lastGame: typeof playerLastGame === 'number' ? playerLastGame : null
          };
        })
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          if (isComplete) {
            if (b.wins !== a.wins) return b.wins - a.wins;
            if (b.draws !== a.draws) return b.draws - a.draws;
            if (a.losses !== b.losses) return a.losses - b.losses;
          }
          return a.name.localeCompare(b.name);
        });
    },
    [draws, isComplete, lastGameScores, losses, playCount, players, scores, wins]
  );

  const leaderboardRows = useMemo(
    () => leaderboard.map((entry) => ({
      name: entry.name,
      score: entry.score,
      wins: entry.wins,
      draws: entry.draws,
      losses: entry.losses,
      playCount: typeof entry.rounds === 'number' ? entry.rounds : null,
      lastRoundScore: entry.lastGame
    })),
    [leaderboard]
  );

  const activePlayers = useMemo(() => {
    const set = new Set();
    displayedGames.forEach((game) => {
      game.team1.players.forEach((p) => set.add(p));
      game.team2.players.forEach((p) => set.add(p));
    });
    return set;
  }, [displayedGames]);

  const benchPlayers = useMemo(() => {
    if (Array.isArray(displayedBenchPlayersFromState)) return displayedBenchPlayersFromState;
    return players.filter((player) => !activePlayers.has(player));
  }, [activePlayers, displayedBenchPlayersFromState, players]);

  const courtAccentColors = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.warning.main,
      theme.palette.success.main,
      theme.palette.info.main
    ],
    [theme.palette.info.main, theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.main]
  );

  const getCourtAccentColor = (index) => courtAccentColors[index % courtAccentColors.length];

  // BroadcastChannel for same-device sync (legacy support)
  useEffect(() => {
    // Skip BroadcastChannel if using Firebase
    if (firebaseId) return;

    if ('BroadcastChannel' in window) {
      channelRef.current = new BroadcastChannel(TV_CHANNEL_NAME);
      channelRef.current.onmessage = (event) => {
        const { name: incomingName, state } = event.data || {};
        if (!incomingName || incomingName !== name || !state) return;
        const decoded = decodeState(state);
        if (decoded) {
          setLiveState(decoded);
        }
      };
    }

    const handleStorage = (event) => {
      if (event.key !== getStorageKey(name) || !event.newValue) return;
      const decoded = decodeState(event.newValue);
      if (decoded) {
        setLiveState(decoded);
      }
    };

    window.addEventListener('storage', handleStorage);

    // Hydrate from any previously stored live state (if tab was opened before a broadcast)
    try {
      const stored = localStorage.getItem(getStorageKey(name));
      if (!liveState && stored) {
        const decoded = decodeState(stored);
        if (decoded) {
          setLiveState(decoded);
        }
      }
    } catch (error) {
      console.warn('Unable to read stored TV state', error);
    }

    return () => {
      window.removeEventListener('storage', handleStorage);
      if (channelRef.current) {
        channelRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, liveState]);

  const hasDecodedState = Boolean(liveState);

  if (!hasDecodedState) {
    return (
      <Paper sx={{ p: { xs: 3, sm: 4 } }}>
        <Typography variant="h5" gutterBottom>
          {translate('tvMode.missingStateTitle')}
        </Typography>
        <Typography sx={{ mb: 3 }}>
          {translate('tvMode.missingStateBody')}
        </Typography>
        <Button variant="contained" color="success" onClick={() => navigate('/new')}>
          {translate('tournament.newTournament')}
        </Button>
      </Paper>
    );
  }

  const leaderboardTable = (
    <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
      <ScoreTable rows={leaderboardRows} translate={translate} includeLastRound size="medium" playerStatus={playerStatus} />
    </TableContainer>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <TvIcon color="primary" fontSize="large" />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              color="primary.main"
              sx={{
                fontSize: { xs: '1.5rem', sm: '2rem' },
                fontWeight: 800,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {translate('tvMode.leaderboard')}
            </Typography>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {tournamentName || name || translate('tournament.name')}
            </Typography>
          </Box>
          <Tooltip
            title={format === 'mexicano'
              ? translate('playerSetup.formatMexicano')
              : translate('playerSetup.formatAmericano')}
            arrow
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
              <FormatIcon format={format} size={36} />
            </Box>
          </Tooltip>
        </Box>

        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip color="secondary" label={translate('tvMode.round', { round })} />
          {roundType === 'time' && localTimeLeft !== null && !isComplete && (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <TimerIcon sx={{ color: localTimeLeft <= 60 ? 'warning.main' : 'text.secondary' }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  fontVariantNumeric: 'tabular-nums',
                  color: localTimeLeft <= 60 ? 'warning.main' : 'text.primary'
                }}
              >
                {formatTime(localTimeLeft)}
              </Typography>
            </Stack>
          )}

          {isComplete && (
            <Chip
              color="success"
              label={translate('tournament.finished')}
              sx={{ fontWeight: 700 }}
            />
          )}
        </Stack>
      </Box>

      {roundType === 'time' && localTimeLeft !== null && !isComplete && roundEnded && (
        <Alert
          severity="warning"
          icon={<TimerIcon />}
          sx={{
            fontSize: '1.1rem',
            fontWeight: 700,
            '& .MuiAlert-message': { width: '100%', textAlign: 'center' }
          }}
        >
          {translate('tournament.timeUp')}
        </Alert>
      )}

      {leaderboardTable}

      {!isComplete && (
        <Paper sx={{ p: { xs: 3, sm: 4 }, borderRadius: 3, overflow: 'hidden' }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2, flexWrap: 'wrap', gap: 1.25 }}
          >
            <Typography variant="h6">{translate('tvMode.onCourt')}</Typography>
            <Chip label={translate('tvMode.round', { round })} color="primary" />
          </Stack>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'grid',
              width: '100%',
              gap: { xs: 1.5, sm: 2 },
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))'
              },
              gridAutoRows: '1fr',
              alignItems: 'stretch'
            }}
          >
            {displayedGames.length === 0 && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography color="text.secondary">
                  {translate('tvMode.noGames')}
                </Typography>
              </Box>
            )}
            {displayedGames.map((game, index) => {
              const accentColor = getCourtAccentColor(index);

              return (
                <Paper
                  variant="outlined"
                  key={game.courtId}
                  sx={{
                    p: 2,
                    width: '100%',
                    boxSizing: 'border-box',
                    minWidth: 0,
                    overflow: 'hidden',
                    display: 'grid',
                    gap: 1.5,
                    borderRadius: 2.5,
                    background: `linear-gradient(180deg, ${alpha(accentColor, theme.palette.mode === 'dark' ? 0.22 : 0.12)} 0%, ${alpha(accentColor, theme.palette.mode === 'dark' ? 0.1 : 0.05)} 100%)`,
                    borderColor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.45 : 0.22),
                    boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.06 : 0.7)}`
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 800,
                        color: accentColor,
                        letterSpacing: 0.2
                      }}
                    >
                      {translate('common.court')} {game.courtId}
                    </Typography>
                  </Stack>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0,
                      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.9 : 0.94),
                      borderColor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.28 : 0.18),
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        sm: 'minmax(0, 1fr) auto'
                      },
                      gap: 1.5,
                      alignItems: 'center',
                      boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.18 : 0.08)}`
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        minWidth: 0,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.35
                      }}
                    >
                      {game.team1.players.join(' • ')}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        minWidth: 48,
                        justifySelf: { xs: 'end', sm: 'auto' },
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 700,
                        flexShrink: 0
                      }}
                    >
                      {game.team1.score ?? '—'}
                    </Typography>
                  </Paper>
                  <Typography
                    variant="caption"
                    sx={{
                      textAlign: 'center',
                      color: alpha(accentColor, theme.palette.mode === 'dark' ? 0.92 : 0.85),
                      fontWeight: 800,
                      letterSpacing: 1.2,
                      py: 0.5
                    }}
                  >
                    VS
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0,
                      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.9 : 0.94),
                      borderColor: alpha(accentColor, theme.palette.mode === 'dark' ? 0.28 : 0.18),
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'minmax(0, 1fr)',
                        sm: 'minmax(0, 1fr) auto'
                      },
                      gap: 1.5,
                      alignItems: 'center',
                      boxShadow: `0 4px 14px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.18 : 0.08)}`
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        minWidth: 0,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        lineHeight: 1.35
                      }}
                    >
                      {game.team2.players.join(' • ')}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        minWidth: 48,
                        justifySelf: { xs: 'end', sm: 'auto' },
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: 700,
                        flexShrink: 0
                      }}
                    >
                      {game.team2.score ?? '—'}
                    </Typography>
                  </Paper>
                </Paper>
              );
            })}
          </Box>
        </Paper>
      )}

      {!isComplete && benchPlayers.length > 0 && (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            {translate('tvMode.bench')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {benchPlayers.map((player) => (
              <Chip key={player} label={player} variant="outlined" />
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );
}

export default TvMode;
